/**
 * FAQ React Query Hooks
 */

import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  createFaq,
  deleteFaq,
  getAdminFaqDetail,
  getAdminFaqList,
  getFaqList,
  updateFaq,
} from '../services/faqService';
import type { CreateFaqRequestDto, FaqCategory, UpdateFaqRequestDto } from '../types/api/faq';
import type { ApiLanguageCode } from '../types/api/i18n-common';

/** 한 번에 조회할 FAQ 개수 (카테고리 구분 없이 전체 조회 후 클라이언트에서 그룹화) */
const FAQ_PAGE_SIZE = 100;

export const faqKeys = {
  all: ['faq'] as const,
  list: (language: ApiLanguageCode) => [...faqKeys.all, 'list', language] as const,
  adminList: (language: ApiLanguageCode, page: number, pageSize: number, category?: FaqCategory) =>
    [...faqKeys.all, 'adminList', language, page, pageSize, category ?? 'ALL'] as const,
  adminDetail: (faqId: number) => [...faqKeys.all, 'adminDetail', faqId] as const,
};

/**
 * 공개 FAQ 리스트 조회 (현재 UI 언어 기준 번역 적용)
 */
export function useFaqList(language: ApiLanguageCode) {
  return useQuery({
    queryKey: faqKeys.list(language),
    queryFn: () => getFaqList({ language, page: 1, pageSize: FAQ_PAGE_SIZE }),
  });
}

/**
 * [어드민] FAQ 리스트 조회 (비활성화 포함, 본문 미포함)
 */
export function useAdminFaqList(
  language: ApiLanguageCode,
  page: number,
  pageSize: number,
  category?: FaqCategory,
) {
  return useQuery({
    queryKey: faqKeys.adminList(language, page, pageSize, category),
    queryFn: () => getAdminFaqList({ category, language, page, pageSize }),
    placeholderData: keepPreviousData,
  });
}

/**
 * [어드민] FAQ 생성 — 성공 시 FAQ 목록(공개/어드민) 캐시 무효화
 */
export function useCreateFaq() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateFaqRequestDto) => createFaq(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: faqKeys.all });
    },
  });
}

/**
 * [어드민] FAQ 상세 조회 (비활성화 포함, ko 번역만 반환)
 */
export function useAdminFaqDetail(faqId: number) {
  return useQuery({
    queryKey: faqKeys.adminDetail(faqId),
    queryFn: () => getAdminFaqDetail(faqId),
    enabled: Number.isInteger(faqId) && faqId > 0,
  });
}

/**
 * [어드민] FAQ 수정 — 성공 시 FAQ 목록·상세(공개/어드민) 캐시 무효화
 */
export function useUpdateFaq() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ faqId, data }: { faqId: number; data: UpdateFaqRequestDto }) => updateFaq(faqId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: faqKeys.all });
    },
  });
}

/**
 * [어드민] FAQ 삭제 — 성공 시 FAQ 목록·상세(공개/어드민) 캐시 무효화
 */
export function useDeleteFaq() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (faqId: number) => deleteFaq(faqId),
    onSuccess: () => {
      // 삭제한 FAQ 의 상세 쿼리는 다시 조회하면 404 이므로 제외하고 무효화한다.
      queryClient.invalidateQueries({
        queryKey: faqKeys.all,
        predicate: (query) => query.queryKey[1] !== 'adminDetail',
      });
    },
  });
}
